import React, { useEffect, useState } from 'react';
import store, { loanActions } from '@apply/adminStore';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import { RepaymentLevelType, RepaymentExitByTypeOptions } from '../LoanConstants';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'devextreme-react/tooltip';

export const LoanPortfolioOverageRepaymentGrid = ({ loanId, editable = true }) => {
    const { dispatch } = store;
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    //ToolTips
    const [instructionsToolTipVisible, set_instructionsToolTipVisible] = useState(false);

    const [repayments, set_repayments] = useState([]);
    const [isEditable, set_isEditable] = useState(false);

    const styles = {
        sectionHeader: { margin: '10px 0' },
        formColumn: { paddingBottom: '5px' },
        formRow: { marginBottom: '0' },
    };

    const loadData = async () => {
        if (!loanId) {
            return;
        }

        const response = await loanApi.getLoanRepaymentsPortfolioOverages(loanId);
        if (response.statusCode !== 200) {
            return;
        }
        set_repayments(response.result.repayments);
        set_isEditable(response.result.isEditable);
    };

    useEffect(() => {
        refreshEvents.addRefreshListener(_afterRefresh);
        return () => {
            refreshEvents.removeRefreshListener(_afterRefresh);
        };
    }, []);

    useEffect(() => {
        async function loanIdChanged() {
            await loadData();
        }

        loanIdChanged();
    }, [loanId]);

    const _afterRefresh = async () => {
        await loadData();
    };

    const editPortfolioOverageRepayment = (event, overrageRepaymentId) => {
        event.preventDefault();
        event.stopPropagation();

        dispatch(
            loanActions.setRepaymentModal({
                repaymentModal: {
                    isOpen: true,
                    entityLevel: RepaymentLevelType.Loan,
                    entityId: loanId,
                    overrageRepaymentId: overrageRepaymentId,
                },
            })
        );
    };

    const actionCellRender = (e) => {
        if (!editable || !isEditable) {
            return null;
        }

        return (
            <button
                id={'action_' + e.data.id}
                className='gridButton'
                title='Edit Loan Overage Repayment'
                onClick={(event) => editPortfolioOverageRepayment(event, e.data.id)}
            >
                Edit
            </button>
        );
    };

    const onPaymentNumberRender = (e) => {
        return e.rowIndex + 1;
    };

    const onExitByRender = (e) => {
        return RepaymentExitByTypeOptions.find((a) => a.id === e.data.exitByTypeId)?.name;
    };

    const renderDate = (e) => {
        if (!e.value) {
            return null;
        }
        return new Date(e.value).toLocaleDateString('en-us');
    };

    if (!loanId || !repayments) {
        return null;
    }

    return (
        <>
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                <h2
                    className='decorated'
                    style={styles.sectionHeader}
                >
                    Portfolio Overage Repayments
                    <div
                        style={{
                            float: 'right',
                            cursor: 'pointer',
                        }}
                        id='portfolioOverageTooltip'
                        onMouseEnter={(e) => set_instructionsToolTipVisible(true)}
                        onMouseLeave={(e) => set_instructionsToolTipVisible(false)}
                    >
                        <FontAwesomeIcon icon={faQuestionCircle} />
                    </div>
                </h2>
                <DataGrid
                    dataSource={repayments}
                    showBorders={true}
                    showRowLines={false}
                    showColumnHeaders={true}
                    rowAlternationEnabled={true}
                    columnMinWidth={50}
                    columnAutoWidth={false}
                    noDataText='No overrage repayments on this portfolio loan'
                >
                    <Column
                        cellRender={onPaymentNumberRender}
                        caption='#'
                        width={20}
                    />
                    <Column
                        dataField='payDate'
                        caption='Date'
                        width={100}
                        cellRender={renderDate}
                    />
                    <Column
                        dataField='amount'
                        caption='Amount Paid'
                        dataType='number'
                        format='currency'
                        width={100}
                        alignment='left'
                    />
                    <Column
                        dataField='remainingPrincipal'
                        caption='Remaining'
                        dataType='number'
                        format='currency'
                        width={100}
                        alignment='left'
                    />
                    <Column
                        caption='Exit By'
                        width={100}
                        alignment='left'
                        cellRender={onExitByRender}
                    />
                    <Column
                        dataField='salePrice'
                        caption='Price'
                        dataType='number'
                        format='currency'
                        width={100}
                        alignment='left'
                    />
                    <Column
                        dataField='refiLenderName'
                        caption='New Lender'
                        width={250}
                    />
                    <Column
                        dataField='notes'
                        caption='Notes'
                    />
                    <Column
                        alignment='center'
                        cellRender={actionCellRender}
                        width={60}
                        allowSorting={false}
                        allowResizing={false}
                        visible={editable && isEditable}
                    />
                </DataGrid>
                <Tooltip
                    target={'#portfolioOverageTooltip'}
                    visible={instructionsToolTipVisible}
                    hideOnOutsideClick={false}
                >
                    <div style={{ width: 'auto', textAlign: 'left' }}>
                        <div
                            style={{
                                width: '100%',
                                margin: '5px 0',
                                whiteSpace: 'break-spaces',
                            }}
                        >
                            For Portfolio Loans - the Loan repayments in excess of Property repayments.
                        </div>
                    </div>
                </Tooltip>
            </div>
        </>
    );
};
